// eslint-disable-next-line prettier/prettier
import React, {
  useRef,
  useState,
  useEffect,
} from 'react';
import { Autocomplete, Icon } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { getSpently } from '../../../util/spentlyRest';
import useDebounce from '../../../util/Debounce';

const ProductsOption = ({ onChange, label }) => {
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [dropDownList, setDropdownList] = useState();
  const [prods, setProds] = useState(null);
  const [productError, setProductError] = useState(false);

  // the debounced input trimed value
  const debouncedInput = useDebounce(filter, 500);

  const debounceRef = useRef(debouncedInput);

  const applyProducts = (prod) => {
    setFilter(prod.label);
    onChange({
      src: prod.image ? prod.image.src : [],
      title: prod.label,
      handle: prod.handle,
    });
  };

  // trigger only on compponent first render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const response = await getSpently(`/api/Products`);
      const products = response.map((p) => ({
        label: p.title,
        value: `${p.id}`,
        handle: p.handle,
        image: p.image,
      }));
      setProds(products);
      setLoading(false);
    };

    if (!prods) {
      loadData();
    } else {
      setProds(prods);
    }
  }, [prods]);

  // update dropdown list when filter or product list changes
  useEffect(() => {
    const loadOptions = () => {
      if (filter !== '') {
        const results = prods.filter((option) =>
          option.label.toLowerCase().includes(filter.toLowerCase())
        );
        setDropdownList(results);
      } else {
        setDropdownList(prods);
      }
    };
    if (prods) {
      loadOptions();
    }
  }, [prods, filter]);

  // when debounced input changes, we want to search for products
  useEffect(() => {
    // send request to search from server
    const SearchProduct = async () => {
      const response = await getSpently(`/api/Product?title=${debouncedInput}`);
      if (response.length > 0) {
        const product = response.map((p) => ({
          label: p.title,
          value: `${p.id}`,
          handle: p.handle,
          image: p.image,
        }));
        setProds(product);
      } else {
        setProductError(`Could not find this product: ${filter}`);
      }
      setLoading(false);
    };

    if (
      debouncedInput &&
      dropDownList &&
      debouncedInput.length &&
      debouncedInput.trim() !== debounceRef.current && // only trigger search if there is a search term
      dropDownList.length === 0 // only trigger search if no products match the search
    ) {
      setLoading(true);

      // do not trigger a new search if the search term is the same as the last search after trim space
      debounceRef.current = debouncedInput.trim();

      SearchProduct();
    }
  }, [debouncedInput, dropDownList, filter, prods, setProds]);

  const textField = (
    <Autocomplete.TextField
      onChange={(txt) => {
        setFilter(txt);
        setProductError(false);
      }}
      selected={filter}
      label={label}
      value={filter}
      prefix={<Icon source={SearchIcon} color="base" />}
      placeholder="Search"
      error={productError}
    />
  );
  return (
    <>
      <Autocomplete
        textField={textField}
        selected={filter}
        onSelect={(s) => {
          const prod = prods.find((p) => p.value === s[0]);
          applyProducts(prod);
        }}
        loading={loading}
        options={dropDownList || []}
      />
    </>
  );
};

export default ProductsOption;
